/**
 * Image Editor Drafts ↔ Company Files bridge (P2).
 *
 * New no-project saves land under Company Files / Library / Image Editor.
 * Legacy `image_editor_drafts` remain readable via fallback until a later cutover.
 * Migration is lazy on Open (first Drafts-tab access) — fits client patterns,
 * no one-shot Admin script required for staging.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  FILES_KIND_FILE,
  FILES_KIND_FOLDER,
  FILES_OWNER_SCOPE_COMPANY,
  FILES_ROOT_PARENT_ID,
  FILES_SOURCE_IMAGE_EDITOR,
  filesNodesCollection,
  listFilesChildren,
  sanitizeFilesNodeName,
} from './companyFiles';

/** Deterministic folder ids under companies/{cid}/files_nodes */
export const FILES_LIBRARY_FOLDER_ID = 'folder_library';
export const FILES_IMAGE_EDITOR_FOLDER_ID = 'folder_image_editor';

export const FILES_LIBRARY_FOLDER_NAME = 'Library';
export const FILES_IMAGE_EDITOR_FOLDER_NAME = 'Image Editor';

/**
 * Ensure Company Files → Library → Image Editor folders exist.
 * Uses getDoc existence probes then setDoc create (rules must allow
 * resource == null gets for studio roles — see files_nodes read rule).
 *
 * @param {string} companyId
 * @param {string|null|undefined} uid
 * @returns {Promise<{ libraryId: string, imageEditorFolderId: string }>}
 */
export async function ensureImageEditorLibraryFolders(companyId, uid = null) {
  const cid = typeof companyId === 'string' ? companyId.trim() : '';
  if (!cid) throw new Error('companyId is required.');

  const libraryRef = doc(db, 'companies', cid, 'files_nodes', FILES_LIBRARY_FOLDER_ID);
  const ieRef = doc(
    db,
    'companies',
    cid,
    'files_nodes',
    FILES_IMAGE_EDITOR_FOLDER_ID,
  );

  /**
   * @param {import('firebase/firestore').DocumentReference} ref
   * @param {Record<string, unknown>} base
   */
  async function ensureFolder(ref, base) {
    const snap = await getDoc(ref);
    if (snap.exists()) return;
    await setDoc(ref, {
      ...base,
      kind: FILES_KIND_FOLDER,
      ownerScope: FILES_OWNER_SCOPE_COMPANY,
      ownerUid: null,
      storagePath: null,
      mimeType: null,
      bytes: null,
      source: null,
      linkedProjectId: null,
      linkedAssetId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdByUid: uid || null,
    });
  }

  await ensureFolder(libraryRef, {
    parentId: FILES_ROOT_PARENT_ID,
    name: FILES_LIBRARY_FOLDER_NAME,
  });
  await ensureFolder(ieRef, {
    parentId: FILES_LIBRARY_FOLDER_ID,
    name: FILES_IMAGE_EDITOR_FOLDER_NAME,
  });

  return {
    libraryId: FILES_LIBRARY_FOLDER_ID,
    imageEditorFolderId: FILES_IMAGE_EDITOR_FOLDER_ID,
  };
}

/**
 * @param {Record<string, unknown>} draft
 * @param {string} draftId
 * @returns {Record<string, unknown>}
 */
function legacyDraftToFilesPayload(draft, draftId) {
  const name = sanitizeFilesNodeName(
    (typeof draft.assetName === 'string' && draft.assetName) ||
      (typeof draft.name === 'string' && draft.name) ||
      draftId,
  );
  const imageUrl =
    (typeof draft.imageUrl === 'string' && draft.imageUrl.trim()) ||
    (typeof draft.previewUrl === 'string' && draft.previewUrl.trim()) ||
    '';
  return {
    kind: FILES_KIND_FILE,
    parentId: FILES_IMAGE_EDITOR_FOLDER_ID,
    name,
    ownerScope: FILES_OWNER_SCOPE_COMPANY,
    ownerUid: null,
    storagePath:
      typeof draft.storagePath === 'string' ? draft.storagePath : null,
    downloadUrl: imageUrl || null,
    mimeType:
      typeof draft.mimeType === 'string'
        ? draft.mimeType
        : 'application/octet-stream',
    bytes: typeof draft.bytes === 'number' ? draft.bytes : null,
    source: FILES_SOURCE_IMAGE_EDITOR,
    linkedProjectId: null,
    linkedAssetId: draftId,
    format: typeof draft.format === 'string' ? draft.format : null,
    width: typeof draft.width === 'number' ? draft.width : null,
    height: typeof draft.height === 'number' ? draft.height : null,
    type: typeof draft.type === 'string' ? draft.type : 'sprite',
    category: typeof draft.category === 'string' ? draft.category : 'prop',
    promptUsed: typeof draft.promptUsed === 'string' ? draft.promptUsed : '',
    updatedAt: serverTimestamp(),
  };
}

/**
 * Lazy-migrate legacy image_editor_drafts into Files (no blob re-upload).
 * Idempotent: skips drafts that already have a files_node with linkedAssetId.
 *
 * @param {string} companyId
 * @param {string|null|undefined} uid
 * @returns {Promise<{ migrated: number, skipped: number }>}
 */
export async function lazyMigrateImageEditorDraftsToFiles(companyId, uid = null) {
  const cid = typeof companyId === 'string' ? companyId.trim() : '';
  if (!cid) throw new Error('companyId is required.');

  await ensureImageEditorLibraryFolders(cid, uid);

  const legacySnap = await getDocs(
    collection(db, 'companies', cid, 'image_editor_drafts'),
  );
  const existing = await listFilesChildren({
    companyId: cid,
    ownerScope: FILES_OWNER_SCOPE_COMPANY,
    parentId: FILES_IMAGE_EDITOR_FOLDER_ID,
  });
  const linked = new Set(
    existing
      .filter((n) => n.kind === FILES_KIND_FILE && n.linkedAssetId)
      .map((n) => String(n.linkedAssetId)),
  );
  // Also treat files_node id that equals legacy draft id as already migrated.
  existing.forEach((n) => {
    if (n.kind === FILES_KIND_FILE) linked.add(n.id);
  });

  let migrated = 0;
  let skipped = 0;
  for (const d of legacySnap.docs) {
    if (linked.has(d.id)) {
      skipped += 1;
      continue;
    }
    const data = d.data() || {};
    const imageUrl =
      (typeof data.imageUrl === 'string' && data.imageUrl.trim()) ||
      (typeof data.previewUrl === 'string' && data.previewUrl.trim()) ||
      '';
    if (!imageUrl && !data.storagePath) {
      skipped += 1;
      continue;
    }
    const payload = {
      ...legacyDraftToFilesPayload(data, d.id),
      createdAt: data.createdAt || serverTimestamp(),
      createdByUid: data.createdByUid || uid || null,
      updatedByUid: data.updatedByUid || uid || null,
    };
    // Stable id keeps Open→Save overwrite working across the cutover.
    await setDoc(doc(filesNodesCollection(cid), d.id), payload, { merge: true });
    migrated += 1;
  }
  return { migrated, skipped };
}

/**
 * Shape a Files node (or legacy draft) for ImageEditorOpenDialog.
 * @param {{ id: string } & Record<string, unknown>} node
 * @param {{ fromLegacyFallback?: boolean }} [opts]
 */
export function toImageEditorDraftOpenItem(node, opts = {}) {
  const imageUrl =
    (typeof node.downloadUrl === 'string' && node.downloadUrl.trim()) ||
    (typeof node.imageUrl === 'string' && node.imageUrl.trim()) ||
    (typeof node.previewUrl === 'string' && node.previewUrl.trim()) ||
    '';
  return {
    id: node.id,
    assetName:
      (typeof node.name === 'string' && node.name) ||
      (typeof node.assetName === 'string' && node.assetName) ||
      node.id,
    imageUrl,
    previewUrl: imageUrl,
    storagePath: node.storagePath || null,
    previewStoragePath: node.previewStoragePath || null,
    format: node.format || null,
    mimeType: node.mimeType || null,
    width: node.width ?? null,
    height: node.height ?? null,
    type: node.type || 'sprite',
    category: node.category || 'prop',
    promptUsed: node.promptUsed || '',
    updatedAt: node.updatedAt || null,
    linkedAssetId: node.linkedAssetId || null,
    source: node.source || FILES_SOURCE_IMAGE_EDITOR,
    editorDocumentKind:
      typeof node.editorDocumentKind === 'string'
        ? node.editorDocumentKind
        : null,
    autosave: node.autosave === true,
    layerCount:
      typeof node.layerCount === 'number' ? node.layerCount : null,
    documentId:
      typeof node.documentId === 'string' ? node.documentId : null,
    ownerScope:
      node.ownerScope === 'user' || node.ownerScope === 'company'
        ? node.ownerScope
        : null,
    ownerUid: typeof node.ownerUid === 'string' ? node.ownerUid : null,
    kind: node.kind || null,
    editorLayerBlobs: Array.isArray(node.editorLayerBlobs)
      ? node.editorLayerBlobs
      : null,
    editorDocumentManifest:
      node.editorDocumentManifest && typeof node.editorDocumentManifest === 'object'
        ? node.editorDocumentManifest
        : null,
    fromLegacyFallback: Boolean(opts.fromLegacyFallback),
  };
}

/**
 * List drafts for Open dialog: Files-backed first, then legacy fallback rows
 * not yet represented in files_nodes.
 *
 * @param {{ companyId: string, uid?: string|null }} params
 */
export async function listImageEditorDraftsForOpen(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const uid = params.uid || null;
  if (!companyId) throw new Error('companyId is required.');

  await lazyMigrateImageEditorDraftsToFiles(companyId, uid);

  const filesChildren = await listFilesChildren({
    companyId,
    ownerScope: FILES_OWNER_SCOPE_COMPANY,
    parentId: FILES_IMAGE_EDITOR_FOLDER_ID,
  });
  const fileNodes = filesChildren.filter((n) => n.kind === FILES_KIND_FILE);
  const fromFiles = fileNodes
    .map((n) => toImageEditorDraftOpenItem(n))
    .filter((a) => Boolean(a.imageUrl));

  const covered = new Set();
  fromFiles.forEach((a) => {
    covered.add(a.id);
    if (a.linkedAssetId) covered.add(String(a.linkedAssetId));
  });

  /** @type {ReturnType<typeof toImageEditorDraftOpenItem>[]} */
  const fromLegacy = [];
  try {
    const legacySnap = await getDocs(
      query(
        collection(db, 'companies', companyId, 'image_editor_drafts'),
        orderBy('updatedAt', 'desc'),
      ),
    );
    for (const d of legacySnap.docs) {
      if (covered.has(d.id)) continue;
      const item = toImageEditorDraftOpenItem(
        { id: d.id, ...d.data() },
        { fromLegacyFallback: true },
      );
      if (!item.imageUrl) continue;
      fromLegacy.push(item);
    }
  } catch (err) {
    // Fallback is best-effort (e.g. missing index) — Files rows still show.
    console.warn('listImageEditorDraftsForOpen legacy fallback:', err);
  }

  const merged = [...fromFiles, ...fromLegacy];
  merged.sort((a, b) => {
    const aMs =
      a.updatedAt?.toMillis?.() || a.updatedAt?.seconds * 1000 || 0;
    const bMs =
      b.updatedAt?.toMillis?.() || b.updatedAt?.seconds * 1000 || 0;
    return bMs - aMs;
  });
  return merged;
}
